import { useCallback, useEffect, useMemo, useState, type CSSProperties, type MouseEvent } from "react";
import {
  FiEdit2,
  FiCheck,
  FiTrash2,
  FiImage,
  FiX,
} from "react-icons/fi";
import "./GridTemplate.css";
import {
  handleProductFileUpload,
  ACCEPTED_IMAGE_TYPES,
  uploadImageFile,
} from "../../../../../../Utils/uploadImage/UploadImage";
import { Upload, UploadProps, Spin, message } from "antd";
import { storeUserLinks, LINK_TITLE_ALREADY_EXISTS } from "../../../../../../Utils/SupabaseServices/UserLinksDatabase";
import { Popover } from "antd";
import { MdDragIndicator } from "react-icons/md";
import track, {
  TRACKING_TYPES,
} from "../../../../../../Utils/SupabaseServices/Track";
import trackTemplate, {
  TEMPLATE_TRACKING_TYPES,
} from "../../../../../../Utils/SupabaseServices/TrackTemplate";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../../../../Utils/SupabaseServices/templateAnalyticsEvents";
import { updateRefClick } from "../../../../../../Utils/SupabaseServices/ContentDatabase";
import { useLocation } from "react-router-dom";
import { addHttpToLink } from "../../../../../../Utils/StayUpToDate/RenderComponent";
import trackEmbed, {
  embedAnalyticsPathname,
  TRACKING_EMBED_TYPES,
} from "../../../../../../Utils/SupabaseServices/TrackEmbed";
import {
  legacyLibAnalyticsPathname,
  shouldMirrorLegacyLibAnalytics,
} from "../../../../../../Utils/analytics/legacyLibAnalyticsPath";
import { isPublicWebsiteTemplateRoute } from "../../../../../../Utils/websiteTemplateGuards";
import { resolveTemplatePageKeyFromPathname } from "../../../../MasterTemplateEditor/websitePublicTemplatePath";
import { useDesign } from "../../../../../../context/DesignContext";
import { getHoverColor } from "./hoverUtils";
import {
  getLinkNameError,
  MAX_LINK_NAME_CHARS,
  trimLinkName,
} from "../linksSectionLimits";
import { useImageCropper } from "../../../../../../hooks/useImageCropper";
import {
  LINK_GRID_CROP,
  type CropViewportState,
} from "../../../../../../Utils/imageCrop/cropImageFile";
import {
  buildPersistedImageCrop,
  getDistinctLinkOriginalUrl,
  getLinkLayoutCropEntry,
  getLinkLayoutDisplayUrl,
  hasLinkOriginalImageChanged,
  isCropViewportCompatible,
  mergeLinkLayoutImageCrop,
  resolveLinkLayoutCropState,
  viewportFromPreset,
} from "../../../../../../Utils/imageCrop/cropStatePersist";
import ImageCropperModal from "../../../../../../components/ImageCropper/ImageCropperModal";
import {
  bannerImageFrameStyle,
  readLinkCoverFrame,
} from "../../../../MasterTemplateEditor/templateBannerUtils";

/** Grid links use LINK_GRID_CROP (143∶92) — keep viewport separate from line. */
const GRID_LINK_CROP_VIEWPORT = viewportFromPreset(LINK_GRID_CROP);
const LINK_LAYOUT = "grid" as const;

function seedGridImageUrl(card: any) {
  return getLinkLayoutDisplayUrl(
    card?.image_crop,
    LINK_LAYOUT,
    card?.image_url,
    card?.original_image_url
  );
}

function gridCoverStyle(card: any): CSSProperties {
  if (getLinkLayoutCropEntry(card?.image_crop, LINK_LAYOUT)) return {};
  const frame = readLinkCoverFrame(card?.image_crop);
  return frame ? bannerImageFrameStyle(frame) || {} : {};
}

function seedGridCropState(card: any) {
  return resolveLinkLayoutCropState(
    card?.image_crop,
    LINK_LAYOUT,
    GRID_LINK_CROP_VIEWPORT
  );
}

const GridTemplate = (props: any) => {
  const {
    card,
    onRemove,
    onRequestDelete,
    username,
    user_id,
    componentName,
    isDashboard,
    refetch,
    editIndex,
    setEditIndex,
    brand_color,
    templates,
    pageFrom,
    componentId,

    designTokens,
    // isNewCard,
    from,
    itemId,
    onDragStart,
    onDragEnd,
    // Optional dnd-kit bindings for the drag handle
    dragAttributes,
    dragListeners,
    isMobileView,
    isLayoutCropProcessing = false,
  } = props;

  const [title, setTitle] = useState(() => card?.link_name || "");
  const [link, setLink] = useState(() => card?.link_url || "");
  const [imageUrl, setImageUrl] = useState<any>(() => seedGridImageUrl(card));
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(
    () => card?.original_image_url || null
  );
  const [linkCropState, setLinkCropState] = useState<CropViewportState | null>(
    () => seedGridCropState(card)
  );
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationErrorLink, setValidationErrorLink] = useState<string | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const onImageUploadStatusChange = props.onImageUploadStatusChange;

  const [isNewCard, setIsNewCard] = useState(card.id === 0);
  const isEditing = editIndex === card.id;

  const {
    cropperOpen,
    cropperImageSrc,
    initialCropState,
    isLoadingOriginal,
    originalSourceUrl,
    closeCropper,
    cancelCropper,
    commitCropSession,
    setOriginalSourceUrl,
    recordCropViewportState,
    openCropperForFile,
    editOriginalImage,
    handleCropReplace,
    replaceInputRef,
  } = useImageCropper({
    aspectRatio: LINK_GRID_CROP.aspectRatio,
    viewportWidth: GRID_LINK_CROP_VIEWPORT.width,
    viewportHeight: GRID_LINK_CROP_VIEWPORT.height,
    defaultFit: "cover",
  });

  useEffect(() => {
    // While this card is open for edit, local state owns image/original — do not
    // clobber an in-session original upload with stale card.original_image_url.
    if (editIndex === card.id) {
      return;
    }
    if (!isNewCard || card.link_name || card.link_url) {
      setTitle(card.link_name);
      setLink(card.link_url);
      setImageUrl(seedGridImageUrl(card));
      setOriginalImageUrl(card.original_image_url || null);
      setLinkCropState(seedGridCropState(card));
    }
  }, [card, editIndex, isNewCard]);

  useEffect(() => {
    // Seed/reset only when the edited card identity changes — not on every
    // card prop refresh (that was wiping in-session original_image_url).
    setTitle(card.link_name || "");
    setLink(card.link_url || "");
    setImageUrl(seedGridImageUrl(card));
    setOriginalImageUrl(card.original_image_url || null);
    setLinkCropState(seedGridCropState(card));
    setValidationError(null);
    setValidationErrorLink(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally seed on edit target change only
  }, [editIndex, card.id]);

  // Seed cropper from persisted uncropped original only (Highlights pattern).
  useEffect(() => {
    if (originalImageUrl) {
      setOriginalSourceUrl(originalImageUrl);
      return;
    }
    if (!imageUrl) {
      setOriginalSourceUrl("");
    }
  }, [originalImageUrl, imageUrl, setOriginalSourceUrl]);

  const isValidUrl = (urlString: string) => {
    try {
      // Always add protocol if missing
      const url = new URL(
        urlString.startsWith("http") ? urlString : "https://" + urlString,
      );
      if (!url.hostname.includes(".")) return false;
      setValidationErrorLink("");
      return true;
    } catch (err) {
      setValidationErrorLink("Please enter a valid URL.");
      return false;
    }
  };

  const handleSave = async (e: any) => {
    e.stopPropagation();

    if (isSaving) return;

    const titleError = getLinkNameError(title);
    if (titleError) {
      setValidationError(titleError);
      return;
    }

    const trimmedTitle = trimLinkName(title).trim();

    if (!link || !link.trim()) {
      setValidationErrorLink("A link is required.");
      return;
    }

    let urlToValidate = link.trim();
    if (!isValidUrl(urlToValidate)) {
      setValidationErrorLink("Please enter a valid URL.");
      return;
    }

    const duplicateTitle = templates.find(
      (template: any) =>
        template.link_name.toLowerCase() === trimmedTitle.toLowerCase() &&
        template.id !== card.id,
    );

    if (duplicateTitle) {
      setValidationError(LINK_TITLE_ALREADY_EXISTS);
      return;
    }

    setIsSaving(true);
    try {
      const built = buildPersistedImageCrop(
        linkCropState,
        GRID_LINK_CROP_VIEWPORT
      );
      const existingEntry = getLinkLayoutCropEntry(
        card.image_crop,
        LINK_LAYOUT
      );
      const existingMatchesLayout =
        Boolean(existingEntry) &&
        (existingEntry!.cropViewport.width === 0 ||
          isCropViewportCompatible(
            existingEntry!.cropViewport,
            GRID_LINK_CROP_VIEWPORT
          ));
      const originalToPersist =
        originalImageUrl ||
        originalSourceUrl ||
        card.original_image_url ||
        null;
      const originalMergeOptions = {
        previousOriginalUrl: card.original_image_url,
        nextOriginalUrl: !imageUrl ? null : originalToPersist,
      };
      // Omit crop write on title-only saves when this layout has no matching crop yet
      // (e.g. legacy flat crop belongs to the other aspect). Never invent a default.
      let persistedCrop:
        | ReturnType<typeof mergeLinkLayoutImageCrop>
        | undefined;
      if (!imageUrl) {
        persistedCrop = mergeLinkLayoutImageCrop(
          card.image_crop,
          LINK_LAYOUT,
          null,
          GRID_LINK_CROP_VIEWPORT,
          originalMergeOptions
        );
      } else if (built || existingMatchesLayout) {
        const layoutEntry = built
          ? { ...built, image_url: imageUrl }
          : {
              cropState: existingEntry!.cropState,
              cropViewport:
                existingEntry!.cropViewport.width > 0
                  ? existingEntry!.cropViewport
                  : GRID_LINK_CROP_VIEWPORT,
              image_url: imageUrl,
            };
        persistedCrop = mergeLinkLayoutImageCrop(
          card.image_crop,
          LINK_LAYOUT,
          layoutEntry,
          GRID_LINK_CROP_VIEWPORT,
          originalMergeOptions
        );
      } else if (
        imageUrl &&
        hasLinkOriginalImageChanged(
          card.original_image_url,
          originalToPersist
        )
      ) {
        // Original replaced but no crop payload — still drop stale line data.
        persistedCrop = mergeLinkLayoutImageCrop(
          card.image_crop,
          LINK_LAYOUT,
          null,
          GRID_LINK_CROP_VIEWPORT,
          originalMergeOptions
        );
      }
      const originalArg = !imageUrl
        ? null
        : originalToPersist || undefined;
      const result = await storeUserLinks(
        card.id === 0 ? null : card.id,
        user_id,
        username,
        trimmedTitle,
        urlToValidate,
        imageUrl,
        componentName,
        from,
        componentId,
        originalArg,
        persistedCrop,
      );
      setValidationError(null);
      setValidationErrorLink(null);
      setIsNewCard(false);
      setEditIndex(undefined);
      refetch();
    } catch (error) {
      console.error("Error saving link:", error);
      const msg = error instanceof Error ? error.message : "";
      setValidationError(
        msg === LINK_TITLE_ALREADY_EXISTS
          ? LINK_TITLE_ALREADY_EXISTS
          : "Failed to save link. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelSave = () => {
    refetch();
    // Only reset values if this is not a new card
    // if (!isNewCard) {
    //   setTitle(card.link_name);
    //   setLink(card.link_url);
    //   setImageUrl(card.image_url);
    // }
    setTitle(card.link_name || "");
    setLink(card.link_url || "");
    setImageUrl(seedGridImageUrl(card));
    setOriginalImageUrl(card.original_image_url || null);
    setLinkCropState(seedGridCropState(card));
    setEditIndex(null);
    setValidationError("");
    setValidationErrorLink("");
  };

  const handleDeleteRequest = (e: any) => {
    e.stopPropagation();
    // Don’t leave validation Popovers open above the shared delete Modal (they use a higher default z-index).
    setValidationError(null);
    setValidationErrorLink(null);
    if (typeof onRequestDelete === "function") {
      onRequestDelete(card.id);
    }
  };

  const uploadCroppedLinkImage = useCallback(
    async (file: File) => {
      setUploading(true);
      onImageUploadStatusChange?.(true);

      try {
        const response = await handleProductFileUpload({
          info: { file, fileList: [] },
          username,
          productName: "",
          setFileList,
          setProductImage: setImageUrl,
          storageName: "mylinks",
        });

        if (response?.publicUrl) {
          setImageUrl(response.publicUrl);
        }
      } catch {
        message.error("Failed to upload image. Please try again.");
      } finally {
        setUploading(false);
        onImageUploadStatusChange?.(false);
      }
    },
    [onImageUploadStatusChange, username],
  );

  const handleCroppedImageSave = useCallback(
    async (file: File, cropState: CropViewportState) => {
      // Always keep the uncropped source so the next Edit opens the same framing.
      const sourceForPersist =
        originalImageUrl || originalSourceUrl || null;
      if (sourceForPersist) {
        setOriginalImageUrl(sourceForPersist);
        if (sourceForPersist !== originalSourceUrl) {
          setOriginalSourceUrl(sourceForPersist);
        }
      }
      recordCropViewportState(cropState);
      setLinkCropState(cropState);
      commitCropSession();
      closeCropper();
      await uploadCroppedLinkImage(file);
    },
    [
      closeCropper,
      commitCropSession,
      originalImageUrl,
      originalSourceUrl,
      recordCropViewportState,
      setOriginalSourceUrl,
      uploadCroppedLinkImage,
    ],
  );

  const handleSelectImageFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        message.error("You can only upload image files!");
        return;
      }

      try {
        setUploading(true);
        onImageUploadStatusChange?.(true);
        const uploadedOriginalUrl = await uploadImageFile({
          file,
          username,
          storageName: "mylinks",
        });
        setOriginalImageUrl(uploadedOriginalUrl);
        setOriginalSourceUrl(uploadedOriginalUrl);
        setLinkCropState(null);
        openCropperForFile(file);
      } catch {
        commitCropSession();
        message.error("Failed to load image. Please try again.");
      } finally {
        setUploading(false);
        onImageUploadStatusChange?.(false);
      }
    },
    [
      commitCropSession,
      onImageUploadStatusChange,
      openCropperForFile,
      setOriginalSourceUrl,
      username,
    ],
  );

  const handleOpenLinkCropper = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      const currentUrl = imageUrl || "";
      if (!currentUrl) {
        replaceInputRef.current?.click();
        return;
      }
      // Crop state is relative to the uncropped bitmap — never restore it on
      // the already-exported display URL (causes extreme / random framing).
      const distinctOriginal = getDistinctLinkOriginalUrl(
        currentUrl,
        originalImageUrl,
        originalSourceUrl,
        card.original_image_url
      );
      const sourceUrl = distinctOriginal || currentUrl;
      void editOriginalImage({
        croppedImageUrl: currentUrl,
        sourceUrl,
        cropState: distinctOriginal ? linkCropState : null,
        // Display-only rows: contain @ 1 so the modal matches the thumb
        // (hook defaultFit cover would invent zoom on the cropped file).
        defaultFit: distinctOriginal ? undefined : "contain",
      });
    },
    [
      card.original_image_url,
      editOriginalImage,
      imageUrl,
      linkCropState,
      originalImageUrl,
      originalSourceUrl,
      replaceInputRef,
    ],
  );

  const handleCropperCancel = useCallback(() => {
    cancelCropper();
    setUploading(false);
    onImageUploadStatusChange?.(false);
  }, [cancelCropper, onImageUploadStatusChange]);

  const handleRemoveImage = useCallback(
    (e?: MouseEvent) => {
      e?.stopPropagation();
      setImageUrl(null);
      setOriginalImageUrl(null);
      setLinkCropState(null);
      setOriginalSourceUrl("");
    },
    [setOriginalSourceUrl]
  );

  const beforeUpload = useCallback(
    (file: File) => {
      if (uploading || cropperOpen || isLoadingOriginal) {
        return Upload.LIST_IGNORE;
      }
      if (!file.type.startsWith("image/")) {
        message.error("You can only upload image files!");
        return Upload.LIST_IGNORE;
      }
      void handleSelectImageFile(file);
      return false;
    },
    [cropperOpen, handleSelectImageFile, isLoadingOriginal, uploading],
  );

  const isImageBusy =
    uploading ||
    cropperOpen ||
    isLoadingOriginal ||
    Boolean(isLayoutCropProcessing);

  const uploadProps: UploadProps = {
    name: "file",
    multiple: false,
    maxCount: 1,
    accept: ACCEPTED_IMAGE_TYPES,
    beforeUpload,
    showUploadList: false,
    disabled: isImageBusy,
  };

  const renderImageWithOverlay = () => (
    <div
      className="grid-template-logo-container"
      style={{
        backgroundColor:isDefaultTheme ? "#FFFFFF" : designTokens?.forthColor ?? "#ffffff"
        // height: isEditing ? "75px" : "var(--grid-image-height)",
      }}
    >
      <img
        src={imageUrl}
        alt={title?.trim() ? `${title.trim()} thumbnail` : "Link thumbnail"}
        className="grid-template-logo"
        loading={isEditing ? undefined : "lazy"}
        decoding="async"
        onClick={(e) => e.stopPropagation()}
        style={{
          ...gridCoverStyle(card),
          pointerEvents: isEditing ? "auto" : "none",
        }}
      />
      {isEditing && (
        <div
          className="grid-template-logo-overlay"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="grid-template-logo-overlay-actions">
            <FiTrash2
              size={20}
              className="grid-template-overlay-icon remove"
              onClick={handleRemoveImage}
              aria-label="Remove link image"
            />
            {isImageBusy ? (
              <Spin
                size="small"
                className="grid-template-upload-spinner-indicator"
              />
            ) : (
              <FiEdit2
                size={20}
                className="grid-template-overlay-icon add"
                onClick={handleOpenLinkCropper}
                aria-label="Crop & Zoom"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderImagePlaceholder = () => (
    <>
      {isEditing ? (
        <div className="grid-template-image-placeholder">
          <Upload {...uploadProps}>
            <div className="grid-template-upload-content">
              {isImageBusy ? (
                <>
                  <Spin
                    size="small"
                    className="grid-template-upload-spinner-indicator"
                  />
                  <span className="grid-template-upload-text">Uploading...</span>
                </>
              ) : (
                <>
                  <FiImage size={32} color="#495057" />
                  <span className="grid-template-upload-text">Upload Image</span>
                </>
              )}
            </div>
          </Upload>
        </div>
      ) : (
        <div className="grid-template-no-image-title">
          <p className="grid-template-field-text title">
            <span
              className={`w-100 grid-template-title ${isMobileView ? "title-mobile" : ""}`}
              style={{ fontFamily: effectiveTokens?.secondaryFontFamily }}
            >
              {title}
            </span>
          </p>
        </div>
      )}
    </>
  );

  const handleEditClick = (e: any) => {
    e.stopPropagation();
    // If this card is already being edited, do nothing
    if (editIndex === card.id) {
      return;
    }
    // Reset any validation errors
    setValidationError(null);
    setValidationErrorLink(null);
    // Reset values to original card values
    setTitle(card.link_name || "");
    setLink(card.link_url || "");
    setImageUrl(seedGridImageUrl(card));
    setOriginalImageUrl(card.original_image_url || null);
    setLinkCropState(seedGridCropState(card));
    // Set the new editIndex
    setEditIndex(card.id);
  };
  const location = useLocation();
  const handleRedirectCheck = (card: any) => {
    const details = {
      post_id: card.id,
      media_url: card.image_url,
      title: componentName,
      name: card.link_name,
      platform: "links-section",
      ...(componentId
        ? { component_id: String(componentId), id: String(componentId) }
        : {}),
    };
    if (pageFrom === "embed-search") {
      trackEmbed("Post Click", TRACKING_EMBED_TYPES.EVENT, {
        pathname: embedAnalyticsPathname(username),
        details,
        user_id: user_id,
        title: "Stay Up To Date",
      });
    } else {
      const path = location?.pathname || "";
      const pageKey =
        resolveTemplatePageKeyFromPathname(path) || "linkInBio";
      if (isPublicWebsiteTemplateRoute(path)) {
        void trackTemplate(
          TEMPLATE_ANALYTICS_EVENTS.POST_CLICK,
          TEMPLATE_TRACKING_TYPES.EVENT,
          {
            pathname: path,
            details,
            user_id,
            username,
            page_key: pageKey,
            section_id: "links",
            section_type: "Links",
            entity_type: "link",
            entity_id: String(card.id || ""),
            entity_label: card.link_name,
            carousel_slug: componentId ? String(componentId) : undefined,
          },
        );
      }
      // Classic LiB tab RPCs — only on LiB surfaces (avoid polluting from other pages).
      if (
        !isPublicWebsiteTemplateRoute(path) ||
        shouldMirrorLegacyLibAnalytics(path, pageKey)
      ) {
        track("Post Click", TRACKING_TYPES.EVENT, {
          pathname: legacyLibAnalyticsPathname(username),
          details,
          user_id: user_id,
          title: "Stay Up To Date",
        });
      }
    }
    updateRefClick(card.id, user_id);
  };
  const {
    templateName,
    primaryColor,
    secondaryColor,
    tertiaryColor,
    fontFamily,
    primaryFont,
    secondaryFont,
    forthColor,
  } = useDesign();
  // Fall back to the live DesignContext when no explicit designTokens prop is
  // passed (dashboard Link in Bio editor) so design panel changes reflect live.
  const effectiveTokens = designTokens ?? {
    primaryColor,
    secondaryColor,
    tertiaryColor,
    fontFamily,
    primaryFontFamily: primaryFont,
    secondaryFontFamily: secondaryFont,
    forthColor,
  };
 
  const designTemplate = designTokens?.templateName
  const effectiveTemplateName = ((designTemplate ?? templateName) || "").toLowerCase();
  const isDefaultTheme = effectiveTemplateName === "default";
  const defaultBorderClass = isDefaultTheme ? "grid-default-theme-border" : "";

  const cardHoverBg = useMemo(() => {
    if (isDefaultTheme) return "#f5f5f7";
    return getHoverColor(
      effectiveTokens?.forthColor,
      effectiveTokens?.secondaryColor,
      brand_color,
    );
  }, [
    brand_color,
    effectiveTokens?.forthColor,
    effectiveTokens?.secondaryColor,
    isDefaultTheme,
  ]);
  return (
    <>
      {isDefaultTheme && (
        <style>{`
          .uptodate-wrapper .grid-default-theme-border { border: 1px solid #ccc !important; }
          /* Hover background for default theme — match line template */
          .uptodate-wrapper .grid-default-theme-border .grid-template-card-front:hover,
          .uptodate-wrapper .click-overlay:hover + .grid-default-theme-border .grid-template-card-front {
            background-color: #f5f5f7 !important;
          }
        `}</style>
      )}
      {isDashboard ? (
        <div
          className={`creator-card ${isEditing && "edit-mode-card"} ${defaultBorderClass}`}
          style={{
            backgroundColor: isDashboard
              ? "#fff"
              : isDefaultTheme
                ? "#fff"
                : effectiveTokens?.forthColor,
            boxShadow: isDashboard
              ? "none"
              : effectiveTokens?.primaryColor
                ? "none"
                : undefined,
            paddingBottom: "0px",
            ["--card-hover-bg" as any]: isDashboard ? "#ebebef" : cardHoverBg,
            
          }}
        >
          <div
            className="grid-template-card-front"
            style={{
              border: effectiveTokens?.secondaryColor
                ? "none"
                : "",
            }}
            onClick={(e) => {
          // On the dashboard, editing should only be triggered via the edit icon button,
          // not by clicking anywhere on the card. For non-dashboard views, keep
          // the existing behavior of opening edit on card click.
          if (
            !isDashboard &&
            (!editIndex || editIndex !== card.id)
          ) {
            e.stopPropagation();
            handleEditClick(e);
          }
            }}
          >
            {(isSaving || isLayoutCropProcessing) && (
              <div className="grid-template-saving-overlay">
                <div className="grid-template-saving-spinner" />
                <span className="grid-template-saving-text">
                  {isLayoutCropProcessing ? "Processing Image..." : "Saving..."}
                </span>
              </div>
            )}
            {isDashboard && (
              <div className="grid-template-actions">
                {isEditing ? (
                  <>
                    <button
                      className="grid-template-save-button"
                      onClick={handleSave}
                      disabled={isSaving || isImageBusy}
                      style={
                        isImageBusy
                          ? { opacity: 0.4, cursor: "not-allowed" }
                          : undefined
                      }
                    >
                      <FiCheck size={20} />
                    </button>

                    <button
                      className="grid-template-cancel-button"
                      disabled={isImageBusy}
                      style={
                        isImageBusy
                          ? { opacity: 0.4, cursor: "not-allowed" }
                          : undefined
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelSave();
                      }}
                    >
                      <FiX size={20} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="line-template-option-button drag-handle"
                      // If dnd-kit listeners are provided, use them instead of
                      // the legacy HTML5 drag events. This keeps the component
                      // backwards-compatible with older drag implementations.
                      draggable={!dragListeners && editIndex !== card.id}
                      onDragStart={
                        dragListeners
                          ? undefined
                          : (e) => {
                              e.stopPropagation();
                              if (onDragStart && itemId !== undefined) {
                                onDragStart(e, itemId);
                              }
                            }
                      }
                      onDragEnd={
                        dragListeners
                          ? undefined
                          : (e) => {
                              e.stopPropagation();
                              if (onDragEnd) {
                                onDragEnd(e);
                              }
                            }
                      }
                      {...(dragAttributes || {})}
                      {...(dragListeners || {})}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <MdDragIndicator
                        size={20}
                        className="drag-handle"
                        style={{ cursor: "grab", color: "black" }}
                      />
                    </button>
                    <button
                      className="line-template-edit-button"
                      onClick={(e) => {
                        if (isDashboard) {
                          e.stopPropagation();
                          handleEditClick(e);
                        }
                      }}
                    >
                      <FiEdit2 size={20} style={{ color: "black" }} />
                    </button>
                  </>
                )}

                {isEditing && (
                  <button
                    className="grid-template-delete-button"
                    disabled={isImageBusy}
                    style={
                      isImageBusy
                        ? { opacity: 0.4, cursor: "not-allowed" }
                        : undefined
                    }
                    onClick={(e) => {
                      handleDeleteRequest(e);
                    }}
                  >
                    <FiTrash2 size={20} />
                  </button>
                )}
              </div>
            )}
            {imageUrl ? (
              <div className="grid-template-image-container">
                {renderImageWithOverlay()}
              </div>
            ) : (
              <>
                {!isEditing ? (
                  title && link ? (
                    <div className="grid-template-no-image-container">
                      <p className="grid-template-field-text title">
                        {" "}
                        <span className={`w-100 grid-template-title ${isMobileView ? "title-mobile" : ""}`} style={{fontFamily: effectiveTokens?.secondaryFontFamily}}>
                          {title}
                        </span>
                      </p>
                    </div>
                  ) : (
                    <div className="grid-template-image-placeholder">
                      <div className="grid-template-upload-content">
                        <FiImage size={32} color="#495057" />
                        <span className="grid-template-upload-text">
                          Upload Image
                        </span>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="grid-template-image-container">
                    {renderImagePlaceholder()}
                  </div>
                )}
              </>
            )}

            <div className="grid-template-content-section">
              <div className="grid-template-input-group" style={isEditing ? {lineHeight:0} :{}}>
                <Popover
                  content={<span className="errorMsg">{validationError}</span>}
                  open={!!validationError}
                  placement="top"
                ></Popover>
                {isEditing ? (
                  <input
                    type="text"
                    value={title}
                    placeholder="Enter title"
                    maxLength={MAX_LINK_NAME_CHARS}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      if (validationError) setValidationError("");
                    }}
                    className="grid-template-edit-input"
                  />
                ) : (
                  <>
                    {imageUrl ? (
                      <>
                        {title ? (
                          <p className="grid-template-field-text title">
                            <span className={`w-100 grid-template-title ${isMobileView ? "title-mobile" : ""}`}  style={{fontFamily: effectiveTokens?.secondaryFontFamily}}>
                              {title}
                            </span>
                          </p>
                        ) : (
                          <p className="grid-template-field-text title-placeholder">
                            Title goes here
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        {" "}
                        {!title && (
                          <p className="grid-template-field-text title-placeholder">
                            Title goes here
                          </p>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
              <Popover
                content={
                  <span className="errorMsg">{validationErrorLink}</span>
                }
                open={!!validationErrorLink}
                placement="top"
              ></Popover>
              {isEditing ? (
                <div className="grid-template-input-group" style={isEditing ? {lineHeight:0} :{}}>
                  <input
                    type="text"
                    value={link}
                    placeholder="Enter link"
                    onChange={(e) => {
                      setLink(e.target.value);
                      if (validationErrorLink) setValidationErrorLink("");
                    }}
                    className="grid-template-edit-input"
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          <div
            className="click-overlay"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 1,
              cursor: "pointer",
            }}
            onClick={() => {
              handleRedirectCheck(card);
              window.open(addHttpToLink(card.link_url), "_blank");
            }}
          />
          <div
            className={`creator-card ${defaultBorderClass}`}
            style={{
              border:isDefaultTheme ? `1px solid #ccc` : "none",
              backgroundColor: isDashboard
                ? "#fff"
                : isDefaultTheme
                  ? "#fff"
                  : effectiveTokens?.forthColor
                    ? effectiveTokens?.forthColor
                    : brand_color,
              boxShadow: effectiveTokens?.secondaryColor ? "none" : undefined,
              paddingBottom: "0px",
              ["--card-hover-bg" as any]: isDashboard ? "#ebebef" : cardHoverBg,
            }}
          >
            <div
              className="grid-template-card-front"
              style={{
                border: effectiveTokens?.secondaryColor
                  ? "none"
                  : "1px solid var(--button-border, #e6e7eb) !important",
              }}
            >
              {(isSaving || isLayoutCropProcessing) && (
                <div className="grid-template-saving-overlay">
                  <div className="grid-template-saving-spinner" />
                  <span className="grid-template-saving-text">
                    {isLayoutCropProcessing ? "Processing Image..." : "Saving..."}
                  </span>
                </div>
              )}
              {imageUrl ? (
                <div className="grid-template-image-container">
                  {renderImageWithOverlay()}
                </div>
              ) : (
                <>
                  {!isEditing ? (
                    title && link ? (
                      <div
                        className="grid-template-no-image-container"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isDashboard) {
                            handleEditClick(e);
                          }
                        }}
                      >
                        <p
                          className="grid-template-field-text title"
                          style={{ color: isDashboard ? undefined : effectiveTokens?.secondaryColor }}
                        >
                          <span className={`w-100 grid-template-title ${isMobileView ? "title-mobile" : ""}`}  style={{fontFamily: effectiveTokens?.secondaryFontFamily}}>
                            {title}
                          </span>
                        </p>
                      </div>
                    ) : (
                      <div className="grid-template-image-placeholder">
                        <div className="grid-template-upload-content">
                          <FiImage size={32} color="#495057" />
                          <span className="grid-template-upload-text">
                            Upload Image
                          </span>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="grid-template-image-container">
                      {renderImagePlaceholder()}
                    </div>
                  )}
                </>
              )}

              <div className="grid-template-content-section">
                <div className="grid-template-input-group" style={isEditing ? {lineHeight:0} :{}}>
                  <Popover
                    content={
                      <span className="errorMsg">{validationError}</span>
                    }
                    open={!!validationError}
                    placement="top"
                  ></Popover>
                  {isEditing ? (
                    <input
                      type="text"
                      value={title}
                      placeholder="Enter title"
                      maxLength={MAX_LINK_NAME_CHARS}
                      onChange={(e) => {
                        setTitle(e.target.value);
                        if (validationError) setValidationError("");
                      }}
                      className="grid-template-edit-input"
                    />
                  ) : (
                    <>
                      {imageUrl ? (
                        <>
                          {title ? (
                            <p
                              className="grid-template-field-text title"
                              style={{ color: isDashboard ? undefined : effectiveTokens?.secondaryColor }}
                            >
                              <span className={`w-100 grid-template-title ${isMobileView ? "title-mobile" : ""}`} style={{fontFamily: effectiveTokens?.secondaryFontFamily}}>
                                {title}
                              </span>
                            </p>
                          ) : (
                            <p className="grid-template-field-text title-placeholder">
                              Title goes here
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          {" "}
                          {!title && (
                            <p className="grid-template-field-text title-placeholder">
                              Title goes here
                            </p>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
                <Popover
                  content={
                    <span className="errorMsg">{validationErrorLink}</span>
                  }
                  open={!!validationErrorLink}
                  placement="top"
                ></Popover>
                {isEditing ? (
                  <div className="grid-template-input-group" style={isEditing ? {lineHeight:0} :{}}>
                    <input
                      type="text"
                      value={link}
                      placeholder="Enter link"
                      onChange={(e) => {
                        setLink(e.target.value);
                        if (validationErrorLink) setValidationErrorLink("");
                      }}
                      className="grid-template-edit-input"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        // </a>
      )}
      <input
        ref={replaceInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            void handleSelectImageFile(file);
          }
          event.target.value = "";
        }}
      />
      <ImageCropperModal
        open={cropperOpen}
        imageSrc={cropperImageSrc}
        preset={LINK_GRID_CROP}
        initialCropState={initialCropState}
        saving={uploading || isLoadingOriginal}
        title="Edit Link Image"
        onCancel={handleCropperCancel}
        onSave={(file, cropState) => void handleCroppedImageSave(file, cropState)}
        onReplace={handleCropReplace}
      />
    </>
  );
};

export default GridTemplate;
